import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GridColDef } from "../../Unknown/DataGrid";
import getColumnAlert from "./columns/getColumnAlert";
import getColumnStatus from "./columns/getColumnStatus";
import getColumnPatient from "./columns/getColumnPatient";
import useTranslations from "./useTranslations";
import {
  AppointmentAIReportColumnItem,
  AppointmentAIReportRowItem,
} from "./types";
import getColumnTime from "./columns/getColumnTime";
import getColumnBtnView from "./columns/getColumnBtnView";
import useStyles from "./useStyles";
import getColumnLocation from "./columns/getColumnLocation";
import getColumnStudies from "./columns/getColumnStudies";
import getColumnBtnReview from "./columns/getColumnBtnReview";

export type DashboardColumnsParams = {
  columnItems: AppointmentAIReportColumnItem[];
  onReviewClick?: (id: number) => void;
  onEditClick?: (id: number) => void;
};

const useDashboardColumns = (params: DashboardColumnsParams) => {
  const { btnHeaderClassName, timeRoot } = useStyles();
  const navigate = useNavigate();
  const { role } = useParams();
  const { columnTranslations } = useTranslations();

  const columns = useMemo((): GridColDef<AppointmentAIReportRowItem>[] => {
    const { columnItems, onReviewClick, onEditClick } = params;

    const innerColumns = columnItems.map((columnItem) => {
      switch (columnItem.field) {
        case "alert": {
          return getColumnAlert({
            columnItem,
            translations: {
              columnAlert: columnTranslations.columnAlert,
              tooltipStat: columnTranslations.tooltipStat,
              tooltipAdditionalViews: columnTranslations.tooltipAdditionalViews,
              tooltipHasPriority: columnTranslations.tooltipHasPriority,
              tooltipContactingPatientRequired:
                columnTranslations.tooltipContactingPatientRequired,
              tooltipHasPreviousAppointments:
                columnTranslations.tooltipHasPreviousAppointments,
            },
          });
        }
        case "status": {
          return getColumnStatus({
            columnItem,
            translations: {
              columnStatus: columnTranslations.columnStatus,
              statusApproved: columnTranslations.statusApproved,
              statusRequiresReview: columnTranslations.statusRequiresReview,
              statusInReview: columnTranslations.statusInReview,
            },
          });
        }
        case "patient": {
          return getColumnPatient({
            columnItem,
            translations: {
              columnPatient: columnTranslations.columnPatient,
            },
            onNameClick: (id: string) =>
              navigate(`/${role}/dashboard/patient/${id}`),
          });
        }
        case "time": {
          return getColumnTime({
            columnItem,
            translations: {
              columnDateTime: columnTranslations.columnDateTime,
            },
            classNames: {
              timeRoot,
            },
          });
        }
        case "location": {
          return getColumnLocation({
            columnItem,
            translations: {
              columnLocation: columnTranslations.columnLocation,
            },
          });
        }
        case "studies": {
          return getColumnStudies({
            columnItem,
            translations: {
              columnStudies: columnTranslations.columnStudies,
            },
          });
        }
        case "btnView": {
          return getColumnBtnView({
            columnItem,
            translations: {
              btnView: columnTranslations.btnView,
            },
            handleClick: (id) => navigate(`/${role}/appointment/${id}`),
            headerClassName: btnHeaderClassName,
          });
        }
        case "btnReview": {
          return getColumnBtnReview({
            columnItem,
            translations: {
              btnReview: columnTranslations.btnReview,
              btnEdit: columnTranslations.btnEdit,
            },
            onReviewClick,
            onEditClick,
            headerClassName: btnHeaderClassName,
          });
        }
        default: {
          return null;
        }
      }
    });

    return innerColumns.filter(
      Boolean,
    ) as GridColDef<AppointmentAIReportRowItem>[];
  }, [
    params,
    columnTranslations.columnAlert,
    columnTranslations.tooltipStat,
    columnTranslations.tooltipAdditionalViews,
    columnTranslations.tooltipHasPriority,
    columnTranslations.tooltipContactingPatientRequired,
    columnTranslations.tooltipHasPreviousAppointments,
    columnTranslations.columnStatus,
    columnTranslations.statusApproved,
    columnTranslations.statusRequiresReview,
    columnTranslations.statusInReview,
    columnTranslations.columnPatient,
    columnTranslations.columnDateTime,
    columnTranslations.columnLocation,
    columnTranslations.columnStudies,
    columnTranslations.btnView,
    columnTranslations.btnReview,
    columnTranslations.btnEdit,
    navigate,
    role,
    timeRoot,
    btnHeaderClassName,
  ]);
  return columns;
};

export default useDashboardColumns;
